import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import FileUploadButton from '../../../components/common/fileUpload';
import { useDispatch, useSelector } from 'react-redux';
import ProductsDropdown from './components/ProductsDropdown';
import { Button, Form } from '@themesberg/react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFileAlt, faSync, faXmark } from '@fortawesome/free-solid-svg-icons';
import NewTable from '../../../components/common/NewTable';
import {
  buildSchedulerObject,
  getCreateRequestColumns,
  getDateFieldsBasedOnChoose,
  getDatesField,
  pricingChoices,
  pricingTemplateMappings,
  productPriceColumns,
  validateDate,
  validatePrice,
} from './utils';
import UploadFile from '../products/components/UploadFile';
import { updateSpinnerState } from '../../../actions/spinner';
import { updateToastInfo } from '../../../actions/settings';
import { apiCall } from '../../../middlewares/api';
import { setProductsMapInStore } from '../../../actions/oms/allProducts';
import { apiCallConsts } from '../../../constants/apiCallConsts';
import { useFieldValidation } from './components/useFieldValidation';
import { convertExcelTimeToIST } from '../../../utils/excel';
import { validatePrebookPrice } from './components/validations';

const CreatePricingRequest = () => {
  const { t } = useTranslation();
  const productsMap = useSelector(
    (state) => state?.allProducts?.productsMap || {}
  );
  const userName = useSelector((state) => state?.user?.user?.username || '');
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [products, setProducts] = useState([]);
  const [copyData, setCopyData] = useState([]);
  const [focusRowId, setFocusRowId] = useState(null);
  const [values, setValues] = useState({});
  const dispatch = useDispatch();
  // const [errors, setErrors] = useState({});
  const { errors, setFieldError, setErrors } = useFieldValidation({});
  // const ThresoldPercentage = 20;

  const fetchProductsData = async (vanNo) => {
    try {
      dispatch(updateSpinnerState(true));
      const productsArray = await apiCall(
        apiCallConsts.GET_METHOD,
        `internal/products/all?vanNo=${vanNo?.map((each) => each)}`
      );
      dispatch(
        setProductsMapInStore({
          ...productsMap,
          ...productsArray,
        })
      );
      return productsArray;
    } catch (e) {
      dispatch(
        updateToastInfo({
          show: true,
          type: 'danger',
          title: 'Failed!',
          message: t(e.message),
        })
      );
    } finally {
      dispatch(updateSpinnerState(false));
    }
  };

  const handleFetchClick = async () => {
    try {
      const vanNos = selectedProducts.map((selected) => selected?.value);
      const products = await fetchProductsData(vanNos);
      const filteredData = copyData.filter((each) =>
        vanNos.includes(each?.vanNo)
      );
      const newData = vanNos.map((vanNo) => {
        const found = filteredData.find((each) => each?.vanNo === vanNo);
        if (found) {
          return {
            ...found,
            vs_price: Number(found.vs_price) || Number(found.vsp),
          };
        } else {
          return {
            ...products[vanNo],
            vs_price: Number(products[vanNo].vsp),
          };
        }
      });
      setProducts(newData);
      setCopyData(newData);
    } catch (error) {
      dispatch(
        updateToastInfo({
          show: true,
          type: 'danger',
          title: 'Failed!',
          message: t(error.message),
        })
      );
    }
    // setProducts(vanNos.map((vanNo) => productsMap[vanNo]));
    // setCopyData(vanNos.map((vanNo) => productsMap[vanNo]))
  };

  const handleXClick = useCallback((id, vanNo) => {
    setProducts((prev) => prev.filter((product) => product.objectId !== id));
    setSelectedProducts((prev) => prev.filter((each) => each.value !== vanNo));

    // setValues((prev) => {
    //   const { [vanNo]: _, ...rest } = prev;
    //   return rest;
    // });
  }, []);

  // console.log(values);
  function getChooseFor(row) {
    // console.log(row);
    if (row[pricingTemplateMappings.OFFER_PRICE] !== '__EMPTY__VALUE__') {
      return pricingChoices.OFFER_PRICE;
    } else if (
      row[pricingTemplateMappings.PREBOOK_PRICE] !== '__EMPTY__VALUE__'
    ) {
      return pricingChoices.PREBOOK_PRICE;
    } else if (
      row[pricingTemplateMappings.COMBINED_PRICE] !== '__EMPTY__VALUE__'
    ) {
      return pricingChoices.COMBI;
    }
    //  else if (
    //   row[pricingTemplateMappings.PRIMARY_SKU_PRICE] !== '__EMPTY__VALUE__'
    // ) {
    //   return pricingChoices.FREEBIE;
    // }
    else {
      return pricingChoices.VSP;
    }
  }

  function excelDateToJSDate(excelDate, time = '00:00') {
    const daysSinceEpoch =
      typeof excelDate === 'number' ? excelDate : Date.parse(excelDate);

    if (isNaN(daysSinceEpoch)) {
      console.error('Invalid date format from Excel:', excelDate);
      return null;
    }

    const millisecondsSinceEpoch = (daysSinceEpoch - 25569) * 86400 * 1000;
    const istOffset = 5.5 * 60 * 60 * 1000;
    const istDate = new Date(millisecondsSinceEpoch - istOffset);

    // Format date in ISO format with time
    const year = istDate.getFullYear();
    const month = String(istDate.getMonth() + 1).padStart(2, '0');
    const day = String(istDate.getDate()).padStart(2, '0');

    return `${year}-${month}-${day}T${time}`;
  }

  const uploadedData = async (fileData = []) => {
    if (!fileData || !Array.isArray(fileData)) {
      throw new Error('Invalid file data');
    }

    const vanNos = [];
    const vanNoSet = new Set(); // Track duplicates
    let hasError = false;

    // First pass: check for empty or duplicate SKUs
    for (const row of fileData) {
      const vanNo = String(row['sku'] || '').trim();
      if (!vanNo) {
        dispatch(
          updateToastInfo({
            show: true,
            type: 'danger',
            title: 'Failed!',
            message: 'Empty SKU found in file',
          })
        );
        setProducts([]);
        setCopyData([]);
        hasError = true;
        break;
      }
      if (vanNoSet.has(vanNo)) {
        dispatch(
          updateToastInfo({
            show: true,
            type: 'danger',
            title: 'Failed!',
            message: `Duplicate SKU ${vanNo} found in file`,
          })
        );
        setProducts([]);
        setCopyData([]);
        hasError = true;
        break;
      }
      vanNoSet.add(vanNo);
      vanNos.push(vanNo);
    }

    // If there was an error in validation, stop further execution
    if (hasError || vanNos.length === 0) {
      return;
    }

    const fileUploadedData = fileData.map((row) => {
      let d = {};
      const choose = getChooseFor(row);
      const field = getDatesField(choose);
      const schedule = { [field]: {} };

      // const priceKey =
      //   choose === pricingChoices.OFFER_PRICE
      //     ? pricingTemplateMappings.OFFER_PRICE
      //     : choose === pricingChoices.PREBOOK_PRICE
      //     ? pricingTemplateMappings.PREBOOK_PRICE
      //     : choose === pricingChoices.COMBI
      //     ? pricingTemplateMappings.COMBINED_PRICE
      //     : 'vs_price';
      // const priceValue = row[priceKey];
      // const priceErr = validatePrice(priceValue, row.mrp, priceKey);
      // if (priceErr) setFieldError(row.sku, priceKey, priceErr);

      // if (choose !== pricingChoices.VSP) {
      //   const dateFields = getDateFieldsBasedOnChoose(choose);
      //   const start = excelDateToJSDate(row[dateFields[0]]);
      //   const end = excelDateToJSDate(row[dateFields[1]]);
      //   const dateErr = validateDate(start, end);
      //   if (dateErr) setFieldError(row.sku, field, dateErr);
      // }

      for (const [key, value] of Object.entries(row)) {
        if (key.toLowerCase().includes('vsp')) {
          d['vs_price'] = String(value);
        } else if (
          choose !== pricingChoices.VSP &&
          (key.toLowerCase().includes('date') ||
            key.toLowerCase().includes('time'))
        ) {
          const dateFields = getDateFieldsBasedOnChoose(choose);
          const schedulerValue = buildSchedulerObject(
            row[dateFields[0]],
            row[dateFields[1]]
          );
          schedule[field] = schedulerValue;
        } else {
          d[key] = String(value);
        }
      }

      return {
        choose,
        ...d,
        ...schedule,
      };
    });

    // Fetch products from system
    const products = await fetchProductsData(vanNos);
    const uploadedData = fileUploadedData.map((each) => {
      return {
        ...(products[each.sku] || {}),
        ...each,
        vanNo: each.sku || each.vanNo,
        vsp: each.vs_price,
      };
    });

    uploadedData.forEach((row) => {
      const choose = row.choose;
      const priceKey =
        choose === pricingChoices.OFFER_PRICE
          ? pricingTemplateMappings.OFFER_PRICE
          : choose === pricingChoices.PREBOOK_PRICE
          ? pricingTemplateMappings.PREBOOK_PRICE
          : choose === pricingChoices.COMBI
          ? pricingTemplateMappings.COMBINED_PRICE
          : 'vs_price';

      // price
      const priceErr = validatePrice(row[priceKey], row.mrp, priceKey);
      if (priceErr) setFieldError(row.objectId, priceKey, priceErr);

      // dates
      if (choose !== pricingChoices.VSP) {
        const field = getDatesField(choose);
        // const { startDateTime, endDateTime } = row[field] || {};
        const dateErr = validateDate(row[field]);
        if (dateErr) setFieldError(row.objectId, field, dateErr);
      }
    });
    // Check if all SKUs exist in system
    const systemSkus = new Set(Object.keys(products));
    const missingSkus = vanNos.filter((sku) => !systemSkus.has(sku));

    if (missingSkus.length > 0) {
      dispatch(
        updateToastInfo({
          show: true,
          type: 'danger',
          title: 'Failed!',
          message: `SKUs not found in system: ${missingSkus.join(', ')}`,
        })
      );
      return;
    }

    // console.log(uploadedData);
    setProducts(uploadedData);
    setCopyData(uploadedData);
  };

  const createRowChangeHandler =
    (setProducts, setCopyData, key) => (row, value) => {
      const updateFn = (prev) =>
        prev.map((product) =>
          product.objectId === row.objectId
            ? { ...product, [key]: value }
            : product
        );

      setProducts(updateFn);
      setCopyData(updateFn);
    };

  const columns = getCreateRequestColumns(
    setProducts,
    setCopyData,
    errors,
    setFieldError,
    focusRowId,
    setFocusRowId,
    createRowChangeHandler,
    handleXClick
  );

  const createRequest = async (data) => {
    try {
      if (Object.keys(errors)?.length > 0) {
        dispatch(
          updateToastInfo({
            show: true,
            type: 'danger',
            title: '',
            message: 'Please correct the data first',
          })
        );
        return;
      }
      dispatch(updateSpinnerState(true));
      const apiresponse = await apiCall('post', 'internal/pricing/add', {
        data,
      });
      setSelectedProducts([]);
      setProducts([]);
      setValues({});
      dispatch(
        updateToastInfo({
          show: true,
          type: 'success',
          title: '',
          message: apiresponse.message,
        })
      );
    } catch (error) {
      dispatch(
        updateToastInfo({
          show: true,
          type: 'danger',
          title: t('failed!'),
          message: error.message,
        })
      );
    } finally {
      dispatch(updateSpinnerState(false));
    }
  };

  const handleSubmitClick = useCallback(() => {
    const errors = {};

    products.forEach((p) => {
      let id = p.objectId;
      let choose = p.choose;
      let errs = { ...errors[id] };
      if (!choose) {
        setFieldError(id, 'choose', 'Choose is required');
        errs['choose'] = 'Choose is required';
      }
      if (!p.vs_price) {
        errs['vs_price'] = 'VS Price is required';
      }
      if (choose !== pricingChoices.VSP) {
        const field = getDatesField(choose);
        let dateErr = '';
        if (p[field]) {
          dateErr = validateDate(p[field]);
        }
        if (dateErr) errs[field] = dateErr;
        const priceKey =
          p.choose === pricingChoices.OFFER_PRICE
            ? pricingTemplateMappings.OFFER_PRICE
            : p.choose === pricingChoices.PREBOOK_PRICE
            ? pricingTemplateMappings.PREBOOK_PRICE
            : p.choose === pricingChoices.COMBI
            ? pricingTemplateMappings.COMBINED_PRICE
            : 'vs_price';

        const priceErr = validatePrice(p[priceKey], p.mrp, priceKey);
        if (priceErr) errs[priceKey] = priceErr;
        if (choose === pricingChoices.PREBOOK_PRICE) {
          const prebookPriceErr = validatePrebookPrice(p.prebook_price, p.mrp);
          if (prebookPriceErr) errs['prebook_price'] = prebookPriceErr;
          if (!p.is_prebook_partial_payment)
            errs['is_prebook_partial_payment'] =
              'Is prebook partial payment is required';
          if (!p?.[pricingTemplateMappings.IS_PREBOOK_PRODUCT])
            errs['is_prebook_partial_payment'] =
              'Is prebook partial payment is required';
        }
      }
      if (Object.keys(errs).length > 0) {
        errors[id] = errs;
      }
    });
    console.log(errors);
    if (Object.keys(errors).length > 0) {
      setErrors(errors);
      console.log('products', products);
      setProducts((prev) => prev.map((p) => ({ ...p, errors })));
      return;
    }

    createRequest({ data: products, userName });
  }, [products, errors, setFieldError, dispatch, userName]);

  console.log(JSON.stringify(errors));
  return (
    <>
      <div className="fw-medium fs-4" style={{ color: 'rgba(68, 68, 68, 1)' }}>
        {t('New Price Change Request')}
      </div>

      <>
        {' '}
        <div className="d-flex justify-content-end mb-2">
          <UploadFile
            uploadedData={uploadedData}
            sampleFileUrl={
              'https://vs-system-templates.s3.ap-south-1.amazonaws.com/pricingRequestsLatest.xlsx'
            }
            buttonText="Import File"
            buttonSize="md"
          />
        </div>
        <div
          className="bg-white p-3 align-content-center"
          style={{ height: '9rem' }}
        >
          <div className="fs-5 text-primary mb-2">{t('VAN No.')}</div>
          <div className="d-flex gap-3">
            <div style={{ minWidth: '23rem' }}>
              <ProductsDropdown
                selectedProducts={selectedProducts}
                setSelectedProducts={setSelectedProducts}
              />
            </div>
            <Button
              className="rounded-0"
              onClick={handleFetchClick}
              size="sm"
              disabled={selectedProducts.length === 0}
            >
              <FontAwesomeIcon icon={faSync} /> &nbsp; {t('FETCH DATA')}
            </Button>
          </div>
        </div>
      </>

      <div className="mt-3">
        <NewTable
          id="productPriceTable"
          data={products}
          // columns={productPriceColumns(handleChange, handleXClick, values)}
          columns={columns}
          isNewEmptyTable={true}
          emptyTable={
            <div className="d-flex flex-column align-items-center text-center my-4">
              <FontAwesomeIcon
                icon={faFileAlt}
                size="2x"
                className="mb-3 text-muted"
              />
              <div className="fw-medium fs-4 mb-2">VAN Number Required</div>
              <div className="fs-6 text-muted">
                Please enter a valid VAN No. to proceed with the price change
                request.
              </div>
            </div>
          }
        />
      </div>
      {products.length > 0 && (
        <div className="mt-4 d-flex justify-content-end">
          <Button
            className="rounded-0 py-3 px-5"
            onClick={handleSubmitClick}
            size="sm"
            disabled={Object.keys(errors).length > 0}
          >
            {t('Submit')}
          </Button>
        </div>
      )}
    </>
  );
};

export default CreatePricingRequest;
